import { requireCompletedProfile } from "@hisani/auth";
import { canEditHorseDetails } from "../domain/membership/authorization-policy.js";
import { canAddHorse } from "../domain/horses/capacity-policy.js";
import { canRecordGelding, canViewFrozenHistory, isLegalLifecycleTransition, isLegalSexEdit, } from "../domain/horses/horse-lifecycle.js";
import { normalizeHorseIdentifier } from "../domain/horses/identifier-fingerprint.js";
import { addHorseSchema, changeHorseLifecycleSchema, getHorseSchema, listHorsesSchema, recordGeldingSchema, stableCapacityLimit, stableConflict, stableForbidden, stableInvalidTransition, stableNotFound, updateHorseSchema, } from "../validation.js";
async function requireHorseEditor(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canEditHorseDetails(membership))
        throw stableForbidden();
    return membership;
}
export async function addHorse(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = addHorseSchema.parse(input);
    await requireHorseEditor(dependencies, parsed.stableId, actor.account.id);
    const dashboard = await dependencies.repository.getStableDashboard(parsed.stableId, actor.account.id);
    if (dashboard === null)
        throw stableNotFound();
    const policy = await dependencies.policy.get();
    const activeHorseCount = await dependencies.repository.countActiveHorses(parsed.stableId);
    if (!canAddHorse({
        subscriptionTier: dashboard.stable.subscriptionTier,
        currentActiveHorseCount: activeHorseCount,
        policy,
    })) {
        throw stableCapacityLimit();
    }
    let fingerprint = null;
    let duplicateWarningShown = false;
    if (parsed.fields.passportNumber !== null) {
        fingerprint = dependencies.identifierFingerprint.fingerprint(normalizeHorseIdentifier(parsed.fields.passportNumber));
        const hasMatch = await dependencies.repository.findPassportFingerprintMatch({
            stableId: parsed.stableId,
            fingerprint,
        });
        if (hasMatch) {
            duplicateWarningShown = true;
            if (!parsed.acknowledgeDuplicateWarning) {
                return {
                    status: "DUPLICATE_WARNING",
                    warningType: "PASSPORT_MATCH",
                };
            }
        }
    }
    const now = dependencies.clock.now();
    const horse = await dependencies.repository.addHorse({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        actorId: actor.account.id,
        fields: parsed.fields,
        passportFingerprint: fingerprint,
        duplicateWarningShown,
        duplicateWarningAcknowledged: parsed.acknowledgeDuplicateWarning,
        scanExtractionId: parsed.scanExtractionId,
        now,
    });
    if (horse === null)
        throw stableConflict();
    return { status: "CREATED", horse };
}
export async function updateHorseDetails(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = updateHorseSchema.parse(input);
    await requireHorseEditor(dependencies, parsed.stableId, actor.account.id);
    const current = await dependencies.repository.getHorse(parsed.stableId, parsed.horseId);
    if (current === null)
        throw stableNotFound();
    if (!isLegalSexEdit(current.sex, parsed.fields.sex)) {
        throw stableInvalidTransition("Gelding is a one-way lifecycle event and cannot be reverted through an edit.");
    }
    const now = dependencies.clock.now();
    const horse = await dependencies.repository.updateHorseDetails({
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        actorId: actor.account.id,
        expectedVersion: parsed.expectedVersion,
        fields: parsed.fields,
        now,
    });
    if (horse === null)
        throw stableConflict();
    return horse;
}
export async function recordGelding(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = recordGeldingSchema.parse(input);
    await requireHorseEditor(dependencies, parsed.stableId, actor.account.id);
    const current = await dependencies.repository.getHorse(parsed.stableId, parsed.horseId);
    if (current === null)
        throw stableNotFound();
    if (!canRecordGelding(current.sex)) {
        throw stableInvalidTransition("This horse is already recorded as Gelding.");
    }
    const now = dependencies.clock.now();
    const horse = await dependencies.repository.recordGelding({
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        actorId: actor.account.id,
        expectedVersion: parsed.expectedVersion,
        geldedOn: parsed.geldedOn,
        now,
    });
    if (horse === null)
        throw stableConflict();
    return horse;
}
export async function changeHorseLifecycleStatus(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = changeHorseLifecycleSchema.parse(input);
    await requireHorseEditor(dependencies, parsed.stableId, actor.account.id);
    const current = await dependencies.repository.getHorse(parsed.stableId, parsed.horseId);
    if (current === null)
        throw stableNotFound();
    if (!isLegalLifecycleTransition(current.lifecycleStatus, parsed.newStatus)) {
        throw stableInvalidTransition(`Cannot change lifecycle status from ${current.lifecycleStatus} to ${parsed.newStatus}.`);
    }
    const now = dependencies.clock.now();
    const horse = await dependencies.repository.changeHorseLifecycleStatus({
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        actorId: actor.account.id,
        expectedVersion: parsed.expectedVersion,
        newStatus: parsed.newStatus,
        now,
    });
    if (horse === null)
        throw stableConflict();
    return horse;
}
/** The canonical single-Horse read path — also the one authorized
 * origin-Stable members use to view a frozen (Transfer-grace/Capacity-limit)
 * Horse's history during its grace period (`canViewFrozenHistory`;
 * `horse-lifecycle.ts`'s doc comment explains why no extra recordState
 * check is needed here). */
export async function getHorse(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getHorseSchema.parse(input);
    const membership = await dependencies.repository.getMembershipContext(parsed.stableId, actor.account.id);
    if (membership === null)
        throw stableNotFound();
    if (!canViewFrozenHistory(membership))
        throw stableForbidden();
    const horse = await dependencies.repository.getHorse(parsed.stableId, parsed.horseId);
    if (horse === null)
        throw stableNotFound();
    return horse;
}
/** Lists every Horse the Stable has ever had, frozen (transferred-out
 * grace-period / over-capacity) ones included — `canViewFrozenHistory`,
 * mirroring `getHorse` above. */
export async function listHorses(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listHorsesSchema.parse(input);
    const membership = await dependencies.repository.getMembershipContext(parsed.stableId, actor.account.id);
    if (membership === null)
        throw stableNotFound();
    if (!canViewFrozenHistory(membership))
        throw stableForbidden();
    return dependencies.repository.listHorses(parsed.stableId);
}
